import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { Industry } from '@/lib/types';

const STAFFING_BOARD_API = '/api/v1/staffing-board';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BoardCertification {
  id: number;
  cert_type: string;
  cert_name: string;
  status: string;
  expiry_date: string | null;
}

export interface BoardSkill {
  id: number;
  skill_name: string;
  proficiency: string;
}

export interface BoardRecentAssignment {
  project_name: string;
  start_date: string | null;
  end_date: string | null;
}

export interface BoardTech {
  id: number;
  tech_id?: number;
  person_id: number;
  first_name: string;
  last_name: string;
  photo: string | null;
  status: string;
  job_title: string;
  // crew-module enhanced fields
  highest_cert_level?: number;
  has_expired_certs?: boolean;
  certifications?: BoardCertification[];
  skills?: BoardSkill[];
  availability_status?: string | null;
  checked_in_today?: boolean;
  recent_assignments?: BoardRecentAssignment[];
  // inventory-module enhanced fields (null: no H3Gear access)
  kit_count?: number | null;
  // payroll-module enhanced fields
  daily_rate?: string | null;
  timesheet_status?: 'draft' | 'submitted' | 'approved' | 'rejected' | null;
  // crew-module packet acknowledgment (S16)
  packet_status?: 'sent' | 'viewed' | 'acknowledged' | null;
  // crew-module travel status (S06)
  travel_status?: 'confirmed' | 'pending' | 'cancelled' | null;
}

export interface BoardProject {
  id: number;
  job_id: number;
  job_number?: string;
  name: string;
  project_number?: string;
  status?: string;
  location: string;
  project_id?: number;
  start_date?: string | null;
  end_date?: string | null;
  // C06 location fields (from location_fk relation)
  location_fk_id?: number | null;
  location_fk_name?: string | null;
  address?: string;
  city?: string;
  state_province?: string;
  // S05 zone/spot muster point
  zone_id?: number | null;
  zone_name?: string | null;
  spot_id?: number | null;
  spot_name?: string | null;
  // H11 coordinate fields for active-job map
  latitude?: number | null;
  longitude?: number | null;
  has_coordinates?: boolean;
  weather_current?: {
    condition: string;
    temperature_c: number;
    temperature_f: number;
    weather_code: number;
  } | null;
  // projects-module enhanced fields
  client_name?: string;
  client_contact_name?: string | null;
  client_contact_phone?: string | null;
  // Budget figures are null for callers without H3Books access.
  budget?: string | null;
  billing_type?: string | null;
  budget_spent?: string | null;
  budget_percent?: number | null;
  budget_utilization?: number | null;
  budget_alert?: boolean;
  staffing_count?: number;
  daily_report_today?: boolean;
  daily_report_url?: string | null;
  supervisor_name?: string | null;
  // PM lens signals (S10). The count is null for callers without H3Books
  // access, who still get the yes/no flag.
  change_orders_pending?: number | null;
  has_change_orders_pending?: boolean;
  delay_flagged?: boolean;
  daily_report_overdue?: boolean;
  // safety-module enhanced fields
  jsa_status?: 'ok' | 'scheduled' | 'inactive' | 'missing';
  jsa_label?: 'JSA OK' | 'JSA unsigned' | 'JSA scheduled' | 'No active JSA' | 'No JSA';
  jsa_signature_count?: string | null;
  jsa_status_color?: 'red' | 'yellow' | 'green';
  jsa_signers?: { tech_id: number; person_name: string; signed_at: string | null }[];
  jsa_url?: string | null;
  jsa_needs_revalidation?: boolean;
  crew_plan_initial_warnings?: Array<{
    plan_type: 'safe_work_plan' | 'rescue_plan';
    plan_revision_id: number | null;
    message: string;
  }>;
  // rescue-plan signal (H3S-F19). 'stale' = signed off over 6 months ago (OQ3):
  // advisory warning only, never a block.
  rescue_current?: boolean;
  rescue_plan_stale?: boolean;
  rescue_plan_status?: 'current' | 'stale' | 'missing';
  // Counts are null for callers without H3Safe access, who still get the
  // yes/no flags (owner ruling, SEC-S9 follow-up).
  open_near_misses?: number | null;
  open_incidents?: number | null;
  has_open_near_misses?: boolean;
  has_open_incidents?: boolean;
  // safety-module weather (from JSA weather_conditions)
  weather?: string | null;
  // inventory-module enhanced fields. Counts are null for callers without
  // H3Gear access, who still get the yes/no flags.
  gear_deployed_count?: number | null;
  gear_inspection_due?: number | null;
  gear_failed_count?: number | null;
  gear_life_exceeded_count?: number | null;
  has_gear_inspection_due?: boolean;
  has_gear_failed?: boolean;
  has_gear_life_exceeded?: boolean;
  has_pending_gear_requests?: boolean;
  gear_link?: string | null;
  gear_request_summary?: {
    total: number;
    requested: number;
    fulfilled: number;
    in_transit: number;
    partially_received: number;
    pending_confirmation: number;
    received: number;
  } | null;
  gear_request_link?: string | null;
  // payroll-module enhanced fields
  daily_crew_cost?: string | null;
  // dispatch evidence
  last_dispatched_at?: string | null;
  dispatch_delivery_status?: 'queued' | 'sent' | 'delivered' | 'failed' | 'no_channel' | null;
  // staffing-requirement enrichment. The requirement is omitted for callers
  // without the H3Books read capability, who still get the yes/no flag.
  has_unmet_staffing_requirement?: boolean;
  staffing_requirement?: {
    id: number;
    required_techs: number;
    required_l1: number;
    required_l2: number;
    required_l3: number;
    required_skills: string[];
    notes?: string;
    satisfied_l1: number;
    satisfied_l2: number;
    satisfied_l3: number;
  };
}

export interface BoardAssignment {
  id: number;
  tech_id: number;
  project_id: number;
  role: string;
  start_date: string;
  end_date: string | null;
  assignment_id?: number;
  packet_status?: 'sent' | 'viewed' | 'acknowledged' | null;
  status?: 'scheduled' | 'active' | 'completed' | 'cancelled' | null;
  validated_via_board?: boolean;
  override_reason?: string;
  override_by?: number | null;
  override_at?: string | null;
  /** Set on assign responses: true when that assign created (or restored) the crew assignment. */
  dispatch_created?: boolean;
}

export interface BoardPlacement {
  id: number;
  person_id: number;
  job_id: number;
  override_reason?: string;
  override_by?: number | null;
  override_at?: string | null;
}

export interface BoardTerminology {
  industry: Industry;
  project_label: string;
  project_label_plural: string;
  location_label: string;
  location_label_plural: string;
  area_label: string;
  area_label_plural: string;
}

export const DEFAULT_TERMINOLOGY: BoardTerminology = {
  industry: 'field_ops',
  project_label: 'Job Site',
  project_label_plural: 'Job Sites',
  location_label: 'Roof',
  location_label_plural: 'Roofs',
  area_label: 'Site',
  area_label_plural: 'Sites',
};

export interface PayrollSummary {
  timesheets_missing: number;
  expenses_pending: number;
}

export interface StaffingBoardData {
  projects: BoardProject[];
  techs: BoardTech[];
  assignments: BoardAssignment[];
  board_placements: BoardPlacement[];
  modules: {
    crew: boolean;
    projects: boolean;
    safety: boolean;
    inventory: boolean;
    payroll: boolean;
    compliance: boolean;
  };
  can_edit: boolean;
  can_dispatch: boolean;
  right_rail: boolean;
  terminology: BoardTerminology;
  payroll_summary?: PayrollSummary;
}

export interface AssignWarning {
  code?: string;
  type: 'availability' | 'overlap' | 'certification' | 'cert_expiry' | 'crew' | 'staffing' | 'schedule';
  message: string;
  tech_id?: number;
}

export interface ConfirmRequiredResponse {
  warnings: AssignWarning[];
  confirm_required: true;
  confirmation_token: string;
  /** True when a certification problem needs a typed override reason. */
  requires_override_reason?: boolean;
  /** False when the current user is not allowed to override certification warnings. */
  can_override?: boolean;
}

export interface BulkConfirmRequiredResponse extends ConfirmRequiredResponse {
  warnings_by_tech?: Record<string, AssignWarning[]>;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

/** Self-heal interval: refetch the board roughly every 60 s even if a WS event is missed. */
const BOARD_REFETCH_INTERVAL = 60_000;

export function useStaffingBoard() {
  return useQuery<StaffingBoardData>({
    queryKey: queryKeys.staffingBoard.all,
    queryFn: async () => {
      const response = await apiClient.get<StaffingBoardData>(
        `${STAFFING_BOARD_API}/`,
      );
      return response.data;
    },
    refetchInterval: BOARD_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function isConfirmRequired<T extends ConfirmRequiredResponse = ConfirmRequiredResponse>(
  data: unknown,
): data is T {
  return (
    typeof data === 'object' &&
    data !== null &&
    'confirm_required' in data &&
    (data as ConfirmRequiredResponse).confirm_required === true
  );
}

export function isConfirmationExpired(error: unknown): boolean {
  return (
    isAxiosError<{ error?: unknown }>(error) &&
    error.response?.status === 400 &&
    error.response.data?.error === 'Confirmation token expired or invalid.'
  );
}

export function useAssignTech(
  onConfirmRequired?: (resp: ConfirmRequiredResponse, originalPayload: {
    tech_id?: number;
    person_id?: number;
    project_id?: number;
    job_id?: number;
    role?: string;
    confirmed?: boolean;
    confirmation_token?: string;
    override_reason?: string;
  }) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      tech_id?: number;
      person_id?: number;
      project_id?: number;
      job_id?: number;
      role?: string;
      confirmed?: boolean;
      confirmation_token?: string;
      override_reason?: string;
    }) => {
      const response = await apiClient.post<BoardAssignment | ConfirmRequiredResponse>(
        `${STAFFING_BOARD_API}/assign/`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      if (isConfirmRequired(data)) {
        onConfirmRequired?.(data, {
          tech_id: variables.tech_id,
          person_id: variables.person_id,
          project_id: variables.project_id,
          job_id: variables.job_id,
          role: variables.role,
          ...(variables.confirmed !== undefined ? { confirmed: variables.confirmed } : {}),
          ...(variables.confirmation_token !== undefined
            ? { confirmation_token: variables.confirmation_token }
            : {}),
          ...(variables.override_reason !== undefined
            ? { override_reason: variables.override_reason }
            : {}),
        });
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

export function useUnassignTech() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      assignment_id?: number;
      person_id?: number;
      job_id?: number;
      /** Undo of an assign: also remove the crew assignment that assign created. */
      undo_assign_dispatch_id?: number;
    }) => {
      await apiClient.post(
        `${STAFFING_BOARD_API}/unassign/`,
        payload,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

export function useRestoreAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      /** BoardPlacement id (or a legacy crew assignment id). Omit to restore by person_id + project_id. */
      assignment_id?: number;
      person_id?: number;
      project_id: number;
      /** The crew assignment the person had; null means they had none. */
      dispatch_id?: number | null;
      role?: string;
      start_date?: string;
      end_date?: string | null;
    }) => {
      const response = await apiClient.post<BoardAssignment>(
        `${STAFFING_BOARD_API}/restore_assignment/`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

export function useMoveTech(
  onConfirmRequired?: (resp: ConfirmRequiredResponse, originalPayload: {
    assignment_id?: number;
    project_id?: number;
    person_id?: number;
    job_id?: number;
    from_job_id?: number;
    confirmed?: boolean;
    confirmation_token?: string;
    override_reason?: string;
  }) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      assignment_id?: number;
      project_id?: number;
      person_id?: number;
      job_id?: number;
      from_job_id?: number;
      confirmed?: boolean;
      confirmation_token?: string;
      override_reason?: string;
    }) => {
      const response = await apiClient.post<BoardAssignment | ConfirmRequiredResponse>(
        `${STAFFING_BOARD_API}/move/`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      if (isConfirmRequired(data)) {
        onConfirmRequired?.(data, {
          assignment_id: variables.assignment_id,
          project_id: variables.project_id,
          person_id: variables.person_id,
          job_id: variables.job_id,
          from_job_id: variables.from_job_id,
          ...(variables.confirmed !== undefined ? { confirmed: variables.confirmed } : {}),
          ...(variables.confirmation_token !== undefined
            ? { confirmation_token: variables.confirmation_token }
            : {}),
          ...(variables.override_reason !== undefined
            ? { override_reason: variables.override_reason }
            : {}),
        });
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

// ─── Bulk Assign ─────────────────────────────────────────────────────────────

export interface BulkAssignResult {
  assigned: BoardAssignment[];
  skipped: { tech_id: number; reason: string }[];
  errors: { tech_id: number; error: string }[];
}

export interface BulkAssignPayload {
  project_id: number;
  tech_ids: number[];
  role?: string;
  confirmed?: boolean;
  confirmation_token?: string;
  override_reason?: string;
}

export function useBulkAssignTechs(
  onConfirmRequired?: (resp: BulkConfirmRequiredResponse, originalPayload: BulkAssignPayload) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BulkAssignPayload) => {
      const response = await apiClient.post<BulkAssignResult | BulkConfirmRequiredResponse>(
        `${STAFFING_BOARD_API}/bulk_assign/`,
        payload,
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      if (isConfirmRequired<BulkConfirmRequiredResponse>(data)) {
        onConfirmRequired?.(data, variables);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

// ─── Crew Lists ──────────────────────────────────────────────────────────────

export interface CrewListItem {
  id: number;
  name: string;
  description: string;
  member_ids: number[];
}

// ─── Timeline ────────────────────────────────────────────────────────────────

export interface TimelineAssignment {
  id: number;
  project_id: number;
  project_name: string;
  project_number: string;
  role: string;
  start_date: string;
  end_date: string | null;
}

export interface TimelineTech {
  id: number;
  tech_id: number | null;
  first_name: string;
  last_name: string;
  job_title: string;
  assignments: TimelineAssignment[];
}

export interface TimelineData {
  start_date: string;
  end_date: string;
  techs: TimelineTech[];
}

export function useTimeline(startDate: string, endDate: string) {
  return useQuery<TimelineData>({
    queryKey: queryKeys.staffingBoard.timeline(startDate, endDate),
    queryFn: async () => {
      const response = await apiClient.get<TimelineData>(
        `${STAFFING_BOARD_API}/timeline/`,
        { params: { start_date: startDate, end_date: endDate } },
      );
      return response.data;
    },
    enabled: !!startDate && !!endDate,
  });
}

// ─── Crew Lists ──────────────────────────────────────────────────────────────

export function useCrewLists(enabled: boolean) {
  return useQuery<CrewListItem[]>({
    queryKey: [...queryKeys.crewLists.all, 'staffing-board'],
    queryFn: async () => {
      const response = await apiClient.get<CrewListItem[]>(
        `${STAFFING_BOARD_API}/crew_lists/`,
      );
      return response.data;
    },
    enabled,
  });
}

// ─── Supervision Cockpit ─────────────────────────────────────────────────────

export interface SupervisionMissingL3 {
  project_id: number;
  project_name: string;
  highest_level: number;
  link: string;
}

// cert_type, cert_name and dates are null for callers who may not see the
// person's certificates; they get one flag row per person instead.
export interface SupervisionCertExpiring {
  tech_id: number;
  tech_name: string;
  cert_type: string | null;
  cert_name: string | null;
  expiry_date: string | null;
  days_until_expiry: number | null;
  link: string;
}

export interface SupervisionCertExpired {
  tech_id: number;
  tech_name: string;
  cert_type: string | null;
  cert_name: string | null;
  expiry_date: string | null;
  link: string;
}

export interface SupervisionNominatedPersonInvalid {
  id: number;
  name: string;
  role: string;
  role_title: string;
  required_cert_level: string;
  link: string;
}

export interface SupervisionCorrectiveAction {
  id: number;
  title: string;
  status: string;
  due_date: string | null;
  is_overdue: boolean;
  assigned_to: string | null;
  source_type: string;
  link: string;
}

export interface SupervisionPendingSafetyApproval {
  id: number;
  title: string;
  alert_type: string;
  severity: string;
  due_date: string | null;
  link: string;
}

export interface SupervisionCockpitData {
  queues: {
    missing_l3: SupervisionMissingL3[];
    cert_expiring: SupervisionCertExpiring[];
    cert_expired: SupervisionCertExpired[];
    nominated_person_invalid: SupervisionNominatedPersonInvalid[];
    open_corrective_actions: SupervisionCorrectiveAction[];
    pending_safety_approvals: SupervisionPendingSafetyApproval[];
  };
  summary: {
    missing_l3_count: number;
    cert_expiring_count: number;
    cert_expired_count: number;
    // null when the caller has no H3Comply access
    nominated_person_invalid_count: number | null;
    open_corrective_actions_count: number | null;
    pending_safety_approvals_count: number | null;
  };
  // Whether each compliance queue has anything waiting; shown without the
  // queue items to callers without H3Comply access.
  flags?: {
    nominated_person_invalid: boolean;
    open_corrective_actions: boolean;
    pending_safety_approvals: boolean;
  };
  modules: {
    crew: boolean;
    compliance: boolean;
  };
  restricted?: {
    crew_certifications: boolean;
    compliance: boolean;
  };
}

export function useSupervisionCockpit(enabled: boolean) {
  return useQuery<SupervisionCockpitData>({
    queryKey: queryKeys.staffingBoard.supervisionCockpit(),
    queryFn: async () => {
      const response = await apiClient.get<SupervisionCockpitData>(
        `${STAFFING_BOARD_API}/supervision_cockpit/`,
      );
      return response.data;
    },
    enabled,
  });
}

// ─── Executive Rollup ────────────────────────────────────────────────────────

export interface BoardRollupData {
  total_jobs: number;
  staffed_jobs: number;
  unstaffed_jobs: number;
  active_projects: number;
  crews_dispatched_today: number;
  total_blockers: number;
  total_warnings: number;
  total_persons: number;
  assigned_persons: number;
  /** Present only for financial roles (Owner/Admin, Manager, payroll ModuleRole). */
  daily_crew_cost?: string;
  /** Compliance posture — present only when the compliance module is enabled (S08). */
  compliance_score?: number;
  open_capa_count?: number;
  open_nc_count?: number;
  days_until_next_assessment?: number | null;
  unresolved_alerts?: number;
  has_critical_alert?: boolean;
}

export function useBoardRollup(enabled: boolean) {
  return useQuery<BoardRollupData>({
    queryKey: queryKeys.staffingBoard.rollup(),
    queryFn: async () => {
      const response = await apiClient.get<BoardRollupData>(
        `${STAFFING_BOARD_API}/rollup/`,
      );
      return response.data;
    },
    enabled,
    refetchInterval: 60_000,
  });
}
